class OrderSearchCriteria {
    constructor() {
        /**
         * Search query.
         *
         * @type {string}
         */
        this.search = '';

        /**
         * Notes search query.
         *
         * @type {string}
         */
        this.notes = '';

        /**
         * Start of the window when the order was created.
         * String in the format 'YYYY-MM-DD'.
         *
         * @type {string|null}
         */
        this.orderDateStart = null;

        /**
         * End of the window when the order was created.
         * String in the format 'YYYY-MM-DD'.
         *
         * @type {string|null}
         */
        this.orderDateEnd = null;

        /**
         * Start of the window when sessions within the order start.
         * String in the format 'YYYY-MM-DD'.
         *
         * @type {string|null}
         */
        this.sessionDateStart = null;

        /**
         * End of the window when sessions within the order start.
         * String in the format 'YYYY-MM-DD'.
         *
         * @type {string|null}
         */
        this.sessionDateEnd = null;

        /**
         * If this is set then only orders containing a ticket for this session will be returned.
         * It also causes a redeem button to be displayed on the order results page.
         *
         * @type {SessionWithEvent|null}
         */
        this.sessionWithEvent = null;

        /**
         * Include cancelled orders in the search results?
         * The value of this is stored in local storage and not cleared when the form is reset.
         *
         * @type {boolean}
         */
        this.includeCancelled = false;

        /**
         * The reason that we are including cancelled orders. The options are:
         * - notes - If the order search form has notes we also include cancelled orders.
         * - ref - If the search looks like a ref we also include cancelled orders.
         * - preference - If the user wants to include cancelled orders.
         *
         * @type {null}
         */
        this.includeCancelledReason = null;

        /**
         * Include unconfirmed orders in the search results?
         * The value of this is stored in local storage and not cleared when the form is reset.
         *
         * @type {boolean}
         */
        this.includeUnconfirmed = false;

        /**
         * The reason that we are including unconfirmed orders. The options are:
         * - ref - If the search looks like a ref we also include unconfirmed orders.
         * - preference - If the user wants to include unconfirmed orders.
         *
         * @type {string|null}
         */
        this.includeUnconfirmedReason = null;

        /**
         * Was this search triggered by scanning a barcode?
         *
         * This doesn't seem to actually be used currently. But it's a good idea to keep for future use.
         *
         * @type {boolean}
         */
        this.wasBarcodeScan = false;

        /**
         * This is used when loading orders for a customer account, no on the redeem / order search page.
         *
         * @type {null}
         */
        this.customerAccountID = null;

        /**
         * Should we filter the search to only orders that are group bookings?
         *
         * @type {boolean}
         */
        this.groupBookingsOnly = false;
    }

    /**
     * @returns {boolean}
     */
    hasSearchFormValues() {
        return !!(
            this.search
            || this.notes
            || this.orderDateStart
            || this.orderDateEnd
            || this.sessionDateStart
            || this.sessionDateEnd
            || this.sessionWithEvent
        );

        // Note that search options such as includeCancelled and groupBookingsOnly are not considered here.
    }

    /**
     * @param {number} minQueryLength
     *
     * @returns {boolean}
     */
    searchFormIsValid(minQueryLength) {
        // A search is valid if:
        // - no search query has been entered (to show recent orders),
        //   or a query has been entered and is at least the minimum length.
        // - or a start date has been selected.
        // - or an end date has been selected.
        // - or some notes have been entered and they are at least the minimum query length.

        if (this.search && this.search.length < minQueryLength) {
            return false;
        }

        if (this.notes && this.notes.length < minQueryLength) {
            return false;
        }

        // At this point it doesn't matter what else is here. We can search with no data or with just one piece.
        return true;
    }

    clear() {
        this.search = '';
        this.notes = '';
        this.orderDateStart = null;
        this.orderDateEnd = null;
        this.sessionDateStart = null;
        this.sessionDateEnd = null;
        this.sessionWithEvent = null;
        // includeCancelled is not cleared.
        // includeUnconfirmed is not cleared.
        this.wasBarcodeScan = false;
        // customerAccountID is not cleared.
        // groupBookingsOnly is not cleared.
    }

    /**
     * Fill this search criteria with the values from another.
     *
     * @param {OrderSearchCriteria} searchCriteria
     */
    fillFrom(searchCriteria) {
        Object.keys(searchCriteria).forEach((key) => {
            if (this.hasOwnProperty(key)) {
                this[key] = searchCriteria[key];
            }
        });
    }

    /**
     * @param {boolean} value
     * @param {string} [reason]
     */
    setIncludeCancelled(value, reason) {
        this.includeCancelled = value;
        if (value) {
            this.includeCancelledReason = reason || null;
        } else {
            this.includeCancelledReason = null;
        }
    }

    /**
     * @param {boolean} value
     * @param {string} [reason]
     */
    setIncludeUnconfirmed(value, reason) {
        this.includeUnconfirmed = value;
        if (value) {
            this.includeUnconfirmedReason = reason || null;
        } else {
            this.includeUnconfirmedReason = null;
        }
    }
}

module.exports = OrderSearchCriteria;
